import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import TableListEditableNew from '@/components/UI/TableListEditable/TableListEditableNew';
import { ERROR, USER_CODE } from '@/helpers/constants';
import useToastContext from '@/hooks/useToastContext';
import usersServices from '@/services/usersServices';
import { UserSearch } from '@/types';
import { mapCampaignManagersRows, tableColumns } from '../SearchTable/search-table-utils';
import TableResetFilter from '../SearchTable/TableResetFilter/TableResetFilter';
import { TABLE_ROW_SELECTION } from '../UI/TableListEditable/TableListEditableConstants';

type DialogSearchSelection = UserSearch[] | UserSearch | string | null;

interface DialogSearchNoRemoteTableProps {
  setSelectedOption: (selectedOption: DialogSearchSelection) => void;
  setSaveDisabled: (disabled: boolean) => void;
  plant?: string;
  role?: string[];
  assignedUser?: string;
}

const DialogSearchNoRemoteTable: React.FC<DialogSearchNoRemoteTableProps> = (props) => {
  const { setSelectedOption, setSaveDisabled, plant, role, assignedUser } = props;
  const { t } = useTranslation();
  const { addToast } = useToastContext();
  const [rows, setRows] = useState<UserSearch[]>([]);
  const [selection, setSelection] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [resetFilters, setResetFilters] = useState(0);
  const { columns } = useMemo(() => tableColumns(t, false, false, false), [t]);

  const handleSelectRow = (newSelection: unknown[]) => {
    const nextSelection = newSelection as string[];
    const selectedValue = nextSelection[0];
    const nextSelectedRows = selectedValue !== undefined ? [selectedValue] : [];

    setSelection(nextSelectedRows);

    if (role) {
      setSelectedOption(rows.filter((row) => row[USER_CODE] === selectedValue) ?? null);
    } else {
      setSelectedOption((selectedValue as string) ?? null);
    }

    setSaveDisabled(nextSelectedRows.length === 0);
  };

  useEffect(() => {
    setLoading(true);

    const request = role
      ? usersServices.getUsersByRole(role)
      : usersServices.getUsersCampaignManager(plant, 'CART_CAMPAIGN_MANAGER', assignedUser);

    request
      .then((res) => {
        setRows(mapCampaignManagersRows(res));
      })
      .catch((error) => addToast(error.message, ERROR))
      .finally(() => setLoading(false));
  }, [addToast, assignedUser, plant, role]);

  return (
    <>
      <TableResetFilter handleFilterChange={() => setResetFilters((prev) => prev + 1)} />
      <TableListEditableNew<UserSearch & Record<string, unknown>>
        isFiltering
        hasCheckbox
        list={rows as (UserSearch & Record<string, unknown>)[]}
        columns={columns}
        columnId={USER_CODE}
        selection={selection}
        rowSelectionMode={TABLE_ROW_SELECTION.SINGLE}
        onSelectionChange={handleSelectRow}
        loading={loading}
        resetFilters={String(resetFilters)}
      />
    </>
  );
};

export default DialogSearchNoRemoteTable;
